import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Briefcase, Plus, Settings, CheckCircle, AlertTriangle, Loader2,
  FileText, Upload, Rocket, Eye, EyeOff, ChevronRight, ChevronDown,
  BookOpen, HelpCircle, Layers, RefreshCw, Trash2, X, Edit, Save
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('session_token');

const ProfessionsManagement = () => {
  const [professions, setProfessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfession, setSelectedProfession] = useState(null);
  const [professionDetails, setProfessionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProfession, setEditingProfession] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [creatingProfession, setCreatingProfession] = useState(false);
  const [newProfession, setNewProfession] = useState({
    name: '',
    country: 'spain',
    region: '',
    category: 'sanidad',
    description: '',
    // Exam config
    total_questions: 100,
    reserve_questions: 10,
    duration_minutes: 150,
    correct_points: 0.6,
    incorrect_penalty: 0.15,
    blank_penalty: 0,
    max_score: 60,
    min_pass_score: 30
  });

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };
  const adminHeaders = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    loadProfessions();
  }, []);

  const loadProfessions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/professions`, { headers });
      setProfessions(res.data.professions || []);
    } catch (error) {
      toast.error('Error al cargar profesiones');
    } finally {
      setLoading(false);
    }
  };

  const loadProfessionDetails = async (professionId) => {
    setLoadingDetails(true);
    setSelectedProfession(professionId);
    try {
      const [detailsRes, readinessRes] = await Promise.all([
        axios.get(`${API}/admin/professions/${professionId}`, { headers }),
        axios.get(`${API}/admin/professions/${professionId}/readiness`, { headers })
      ]);
      setProfessionDetails(detailsRes.data);
      setReadiness(readinessRes.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePublish = async (professionId, publish, commercialize = false) => {
    setPublishing(true);
    try {
      const res = await axios.post(
        `${API}/admin/professions/${professionId}/publish`,
        { publish, commercialize },
        { headers }
      );
      toast.success(res.data.message);
      if (res.data.warnings?.length > 0) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      loadProfessions();
      loadProfessionDetails(professionId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar');
    } finally {
      setPublishing(false);
    }
  };

  const handleGenerateContent = async (professionId) => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/professions/${professionId}/generate-content`,
        {},
        { headers }
      );
      toast.success(res.data.message);
      loadProfessionDetails(professionId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar contenido');
    } finally {
      setGenerating(false);
    }
  };

  const deleteProfession = async (professionId, professionName) => {
    if (!window.confirm(`¿Eliminar la profesión "${professionName}"? Esta acción eliminará todos los temas, preguntas y materiales asociados.`)) return;
    try {
      await axios.delete(`${API}/admin/professions/${professionId}`, { headers: adminHeaders });
      toast.success('Profesión eliminada');
      loadProfessions();
      if (selectedProfession === professionId) {
        setSelectedProfession(null);
        setProfessionDetails(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar profesión');
    }
  };

  const createProfession = async () => {
    if (!newProfession.name) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setCreatingProfession(true);
    try {
      const res = await axios.post(`${API}/admin/professions`, newProfession, { headers: adminHeaders });
      toast.success('Profesión creada correctamente');
      setShowCreateModal(false);
      setNewProfession({ 
        name: '', country: 'spain', region: '', category: 'sanidad', description: '',
        total_questions: 100, reserve_questions: 10, duration_minutes: 150,
        correct_points: 0.6, incorrect_penalty: 0.15, blank_penalty: 0,
        max_score: 60, min_pass_score: 30
      });
      loadProfessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear profesión');
    } finally {
      setCreatingProfession(false);
    }
  };

  const openEditModal = (prof) => {
    setEditingProfession({
      profession_id: prof.profession_id,
      name: prof.name || '',
      category: prof.category || 'sanidad',
      country_code: prof.country_code || 'ES',
      comunidad_autonoma: prof.comunidad_autonoma || '',
      description: prof.description || '',
      is_active: prof.is_active !== false,
      is_published: prof.is_published || false,
      is_commercialized: prof.is_commercialized || false,
      // Exam config
      total_questions: prof.exam_config?.total_questions || prof.total_questions || 100,
      reserve_questions: prof.exam_config?.reserve_questions || prof.reserve_questions || 10,
      duration_minutes: prof.exam_config?.time_limit_minutes || prof.duration_minutes || 150,
      correct_points: prof.exam_config?.correct_points || prof.correct_points || 0.6,
      incorrect_penalty: prof.exam_config?.incorrect_points ? Math.abs(prof.exam_config.incorrect_points) : (prof.incorrect_penalty || 0.15),
      blank_penalty: prof.exam_config?.blank_points || prof.blank_penalty || 0,
      max_score: prof.exam_config?.max_score || prof.max_score || 60,
      min_pass_score: prof.exam_config?.passing_score || prof.min_pass_score || 30
    });
    setShowEditModal(true);
  };

  const saveEditProfession = async () => {
    if (!editingProfession?.name) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSavingEdit(true);
    try {
      // 1. Update profession basic info
      await axios.put(
        `${API}/admin/professions/${editingProfession.profession_id}`,
        {
          name: editingProfession.name,
          description: editingProfession.description,
          category: editingProfession.category,
          is_active: editingProfession.is_active,
          is_published: editingProfession.is_published,
          is_commercialized: editingProfession.is_commercialized,
          comunidad_autonoma: editingProfession.comunidad_autonoma,
          country_code: editingProfession.country_code
        },
        { headers: adminHeaders }
      );
      
      // 2. Update exam config if any exam fields are present
      if (editingProfession.total_questions || editingProfession.duration_minutes) {
        await axios.put(
          `${API}/admin/professions/${editingProfession.profession_id}/exam-config`,
          {
            total_questions: editingProfession.total_questions,
            reserve_questions: editingProfession.reserve_questions,
            time_limit_minutes: editingProfession.duration_minutes,
            correct_points: editingProfession.correct_points,
            incorrect_points: -Math.abs(editingProfession.incorrect_penalty || 0), // Negative value
            blank_points: editingProfession.blank_penalty || 0,
            passing_score: editingProfession.min_pass_score,
            max_score: editingProfession.max_score
          },
          { headers: adminHeaders }
        );
      }
      
      toast.success('Profesión y configuración de examen actualizadas');
      setShowEditModal(false);
      setEditingProfession(null);
      loadProfessions();
      if (selectedProfession === editingProfession.profession_id) {
        loadProfessionDetails(editingProfession.profession_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar profesión');
    } finally {
      setSavingEdit(false);
    }
  };

  const getStatusBadge = (prof) => {
    // First check if profession is inactive (deleted)
    if (prof.is_active === false) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">🗑️ Eliminada</span>;
    }
    if (prof.is_commercialized) {
      return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">🚀 Comercializada</span>;
    }
    if (prof.is_published) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">👁️ Publicada</span>;
    }
    return <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">📝 Borrador</span>;
  };

  const reactivateProfession = async (professionId) => {
    try {
      await axios.put(`${API}/admin/professions/${professionId}`, 
        { is_active: true },
        { headers: adminHeaders }
      );
      toast.success('Profesión reactivada');
      loadProfessions();
    } catch (error) {
      toast.error('Error al reactivar profesión');
    }
  };

  const getReadinessColor = (score) => {
    if (score >= 80) return 'emerald';
    if (score >= 60) return 'amber';
    return 'red';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-indigo-600" />
            Gestión de Profesiones
          </h2>
          <p className="text-slate-600">Administra las oposiciones y su publicación</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadProfessions} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Profesión
          </Button>
        </div>
      </div>

      {/* Create Profession Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Crear Nueva Profesión</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre de la Oposición *</label>
                <Input
                  value={newProfession.name}
                  onChange={(e) => setNewProfession({...newProfession, name: e.target.value})}
                  placeholder="Ej: Enfermero/a de Urgencias"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">País</label>
                  <select
                    value={newProfession.country}
                    onChange={(e) => setNewProfession({...newProfession, country: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="spain">España</option>
                    <option value="mexico">México</option>
                    <option value="argentina">Argentina</option>
                    <option value="colombia">Colombia</option>
                    <option value="peru">Perú</option>
                    <option value="chile">Chile</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Región/CCAA</label>
                  <Input
                    value={newProfession.region}
                    onChange={(e) => setNewProfession({...newProfession, region: e.target.value})}
                    placeholder="Ej: Madrid, Cataluña..."
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Categoría</label>
                <select
                  value={newProfession.category}
                  onChange={(e) => setNewProfession({...newProfession, category: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="sanidad">Sanidad</option>
                  <option value="educacion">Educación</option>
                  <option value="administracion">Administración</option>
                  <option value="justicia">Justicia</option>
                  <option value="seguridad">Seguridad</option>
                  <option value="otros">Otros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  value={newProfession.description}
                  onChange={(e) => setNewProfession({...newProfession, description: e.target.value})}
                  placeholder="Descripción breve de la oposición..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg h-20"
                />
              </div>
              
              {/* Exam Configuration */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Configuración del Examen
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Nº Preguntas</label>
                    <Input
                      type="number"
                      value={newProfession.total_questions}
                      onChange={(e) => setNewProfession({...newProfession, total_questions: parseInt(e.target.value) || 100})}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Preguntas Reserva</label>
                    <Input
                      type="number"
                      value={newProfession.reserve_questions}
                      onChange={(e) => setNewProfession({...newProfession, reserve_questions: parseInt(e.target.value) || 10})}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Duración (min)</label>
                    <Input
                      type="number"
                      value={newProfession.duration_minutes}
                      onChange={(e) => setNewProfession({...newProfession, duration_minutes: parseInt(e.target.value) || 150})}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Puntos Acierto</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newProfession.correct_points}
                      onChange={(e) => setNewProfession({...newProfession, correct_points: parseFloat(e.target.value) || 0.6})}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Penalización Error</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newProfession.incorrect_penalty}
                      onChange={(e) => setNewProfession({...newProfession, incorrect_penalty: parseFloat(e.target.value) || 0.15})}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Nota Mínima Aprobar</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newProfession.min_pass_score}
                      onChange={(e) => setNewProfession({...newProfession, min_pass_score: parseFloat(e.target.value) || 30})}
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={createProfession}
                disabled={creatingProfession}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {creatingProfession ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Crear Profesión
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lista de profesiones */}
        <div className="lg:col-span-1 space-y-3">
          <h3 className="font-bold text-sm text-slate-500 uppercase tracking-wide">
            Profesiones ({professions.length})
          </h3>
          
          {professions.map((prof) => (
            <motion.div
              key={prof.profession_id}
              whileHover={{ scale: 1.01 }}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedProfession === prof.profession_id
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 bg-white hover:border-indigo-300'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 
                  onClick={() => loadProfessionDetails(prof.profession_id)}
                  className="font-medium text-slate-900 text-sm flex-1"
                >
                  {prof.name}
                </h4>
                <div className="flex items-center gap-2">
                  {getStatusBadge(prof)}
                  {prof.is_active === false ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        reactivateProfession(prof.profession_id);
                      }}
                      className="p-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                      title="Reactivar profesión"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(prof);
                        }}
                        className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                        title="Editar profesión"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteProfession(prof.profession_id, prof.name);
                        }}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Eliminar profesión"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <div 
                onClick={() => loadProfessionDetails(prof.profession_id)}
                className="flex items-center gap-4 text-xs text-slate-500"
              >
                <span className="flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  {prof.stats?.topics || 0} temas
                </span>
                <span className="flex items-center gap-1">
                  <HelpCircle className="w-3 h-3" />
                  {prof.stats?.questions || 0} preguntas
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Detalles de la profesión */}
        <div className="lg:col-span-2">
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : professionDetails ? (
            <div className="space-y-6">
              {/* Info básica */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {professionDetails.profession.name}
                    </h3>
                    <p className="text-slate-500">
                      {professionDetails.profession.comunidad_autonoma || professionDetails.profession.country_code}
                    </p>
                  </div>
                  {getStatusBadge(professionDetails.profession)}
                </div>

                {/* Readiness Score */}
                {readiness && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Preparación para comercialización</span>
                      <span className={`text-lg font-bold text-${getReadinessColor(readiness.readiness_score)}-600`}>
                        {readiness.readiness_score}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className={`bg-${getReadinessColor(readiness.readiness_score)}-500 h-3 rounded-full transition-all`}
                        style={{ width: `${readiness.readiness_score}%` }}
                      />
                    </div>
                    
                    {/* Checklist */}
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {Object.entries(readiness.checks).map(([key, passed]) => (
                        <div key={key} className={`flex items-center gap-2 text-sm ${passed ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {passed ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                          <span>{key.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleGenerateContent(professionDetails.profession.profession_id)}
                    disabled={generating}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Generar Flashcards
                  </Button>
                  
                  {!professionDetails.profession.is_published ? (
                    <Button
                      onClick={() => handlePublish(professionDetails.profession.profession_id, true, false)}
                      disabled={publishing}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {publishing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Publicar
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handlePublish(professionDetails.profession.profession_id, false, false)}
                      disabled={publishing}
                      variant="outline"
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      Despublicar
                    </Button>
                  )}
                  
                  {professionDetails.profession.is_published && !professionDetails.profession.is_commercialized && (
                    <Button
                      onClick={() => handlePublish(professionDetails.profession.profession_id, true, true)}
                      disabled={publishing || (readiness?.readiness_score || 0) < 60}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Rocket className="w-4 h-4 mr-2" />
                      🚀 Comercializar
                    </Button>
                  )}
                </div>
              </div>

              {/* Exam Config */}
              {professionDetails.profession.exam_config && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h4 className="font-bold mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-indigo-600" />
                    Configuración del Examen
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-indigo-600">
                        {professionDetails.profession.exam_config.total_questions}
                      </p>
                      <p className="text-xs text-slate-500">Preguntas</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {professionDetails.profession.exam_config.reserve_questions}
                      </p>
                      <p className="text-xs text-slate-500">Reserva</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {professionDetails.profession.exam_config.duration_minutes}'
                      </p>
                      <p className="text-xs text-slate-500">Duración</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {professionDetails.profession.exam_config.scoring?.min_pass_score}/{professionDetails.profession.exam_config.scoring?.max_score}
                      </p>
                      <p className="text-xs text-slate-500">Aprobado</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h4 className="font-bold mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                  Estadísticas de Contenido
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">
                      {professionDetails.stats?.total_topics || 0}
                    </p>
                    <p className="text-xs text-slate-500">Temas Total</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-600">
                      {professionDetails.stats?.topics_with_questions || 0}
                    </p>
                    <p className="text-xs text-slate-500">Con Preguntas</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      {professionDetails.stats?.topics_without_questions || 0}
                    </p>
                    <p className="text-xs text-slate-500">Sin Preguntas</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {professionDetails.stats?.total_questions || 0}
                    </p>
                    <p className="text-xs text-slate-500">Preguntas</p>
                  </div>
                </div>
              </div>

              {/* Topics List */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h4 className="font-bold mb-4 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-indigo-600" />
                  Temas ({professionDetails.topics?.length || 0})
                </h4>
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {professionDetails.topics?.map((topic, idx) => (
                    <div
                      key={topic.topic_id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        topic.questions_count > 0 ? 'bg-emerald-50' : 'bg-slate-50'
                      }`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm text-slate-900">
                          {topic.name}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <span className={`px-2 py-1 rounded-full ${
                          topic.questions_count > 0
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          {topic.questions_count} preg
                        </span>
                        <span className={`px-2 py-1 rounded-full ${
                          topic.flashcards_count > 0
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          {topic.flashcards_count} flash
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Selecciona una profesión para ver los detalles</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Profession Modal */}
      {showEditModal && editingProfession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">Editar Profesión</h3>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre de la Oposición *</label>
                <Input
                  value={editingProfession.name}
                  onChange={(e) => setEditingProfession({...editingProfession, name: e.target.value})}
                  placeholder="Ej: Enfermería de Urgencias de Atención Primaria"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Categoría</label>
                  <select
                    value={editingProfession.category}
                    onChange={(e) => setEditingProfession({...editingProfession, category: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-700"
                  >
                    <option value="sanidad">🏥 Sanidad</option>
                    <option value="administracion">📋 Administración</option>
                    <option value="seguridad">👮 Seguridad</option>
                    <option value="justicia">⚖️ Justicia</option>
                    <option value="educacion">📚 Educación</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Comunidad Autónoma</label>
                  <select
                    value={editingProfession.comunidad_autonoma}
                    onChange={(e) => setEditingProfession({...editingProfession, comunidad_autonoma: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-700"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="baleares">Islas Baleares</option>
                    <option value="madrid">Madrid</option>
                    <option value="cataluna">Cataluña</option>
                    <option value="andalucia">Andalucía</option>
                    <option value="valencia">Comunidad Valenciana</option>
                    <option value="galicia">Galicia</option>
                    <option value="pais_vasco">País Vasco</option>
                    <option value="castilla_leon">Castilla y León</option>
                    <option value="canarias">Canarias</option>
                    <option value="aragon">Aragón</option>
                    <option value="murcia">Murcia</option>
                    <option value="extremadura">Extremadura</option>
                    <option value="asturias">Asturias</option>
                    <option value="cantabria">Cantabria</option>
                    <option value="navarra">Navarra</option>
                    <option value="la_rioja">La Rioja</option>
                    <option value="castilla_mancha">Castilla-La Mancha</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  value={editingProfession.description}
                  onChange={(e) => setEditingProfession({...editingProfession, description: e.target.value})}
                  placeholder="Descripción breve de la oposición..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg h-20"
                />
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingProfession.is_active}
                    onChange={(e) => setEditingProfession({...editingProfession, is_active: e.target.checked})}
                    className="rounded border-slate-300 text-indigo-600"
                  />
                  <span className="text-sm text-slate-700">Activa</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingProfession.is_published}
                    onChange={(e) => setEditingProfession({...editingProfession, is_published: e.target.checked})}
                    className="rounded border-slate-300 text-green-600"
                  />
                  <span className="text-sm text-slate-700">Publicada</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingProfession.is_commercialized}
                    onChange={(e) => setEditingProfession({...editingProfession, is_commercialized: e.target.checked})}
                    className="rounded border-slate-300 text-amber-600"
                  />
                  <span className="text-sm text-slate-700">Comercializada</span>
                </label>
              </div>

              {/* Configuración del Examen */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Configuración del Tipo de Examen
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Total Preguntas</label>
                    <Input
                      type="number"
                      value={editingProfession.total_questions || 100}
                      onChange={(e) => setEditingProfession({...editingProfession, total_questions: parseInt(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Preguntas Reserva</label>
                    <Input
                      type="number"
                      value={editingProfession.reserve_questions || 10}
                      onChange={(e) => setEditingProfession({...editingProfession, reserve_questions: parseInt(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Duración (minutos)</label>
                    <Input
                      type="number"
                      value={editingProfession.duration_minutes || 150}
                      onChange={(e) => setEditingProfession({...editingProfession, duration_minutes: parseInt(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Nota Mínima Aprobar</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingProfession.min_pass_score || 30}
                      onChange={(e) => setEditingProfession({...editingProfession, min_pass_score: parseFloat(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Puntos Correcta</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProfession.correct_points || 0.6}
                      onChange={(e) => setEditingProfession({...editingProfession, correct_points: parseFloat(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Penalización Incorrecta</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProfession.incorrect_penalty || 0.15}
                      onChange={(e) => setEditingProfession({...editingProfession, incorrect_penalty: parseFloat(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Penalización Blanco</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProfession.blank_penalty || 0}
                      onChange={(e) => setEditingProfession({...editingProfession, blank_penalty: parseFloat(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Puntuación Máxima</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingProfession.max_score || 60}
                      onChange={(e) => setEditingProfession({...editingProfession, max_score: parseFloat(e.target.value) || 0})}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={saveEditProfession}
                disabled={savingEdit}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionsManagement;
